import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from 'reactstrap';
import { oracleCardsService } from '../services';
import { OracleCard } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import DeleteModal from '../components/Common/DeleteModal';
import { toast } from 'react-toastify';

// ─── Main Image Panel ────────────────────────────────────────────────────────

const MainImagePanel: React.FC = () => {
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    oracleCardsService.getSettings()
      .then((s) => setCurrentImage(s.mainImage))
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await oracleCardsService.updateMainImage(file);
      setCurrentImage(res.mainImage);
      toast.success('Section image updated');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="mb-4">
      <CardBody>
        <h5 className="card-title mb-1">
          <i className="ri-image-line me-2"></i>Section Main Image
        </h5>
        <p className="text-muted small mb-3">
          This image appears on the home screen for the Oracle Card section.
        </p>
        <div className="d-flex align-items-start gap-4 flex-wrap">
          <div
            style={{
              width: 140,
              height: 140,
              borderRadius: 10,
              border: '1.5px dashed #ced4da',
              background: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {loadingSettings ? (
              <Spinner size="sm" color="secondary" />
            ) : currentImage ? (
              <img
                src={currentImage}
                alt="Section main"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span className="text-muted small text-center px-2">No image set</span>
            )}
          </div>
          <div className="d-flex flex-column gap-2 justify-content-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              color="primary"
              outline
              disabled={uploading || loadingSettings}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <><Spinner size="sm" className="me-1" /> Uploading…</>
              ) : (
                <><i className="ri-upload-2-line me-1"></i>{currentImage ? 'Replace Image' : 'Upload Image'}</>
              )}
            </Button>
            <span className="text-muted small">JPEG, PNG or WEBP</span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

function cardThumb(card: OracleCard): string | undefined {
  return card.frontImage || card.image;
}

const OracleCardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<OracleCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;

  const [activeModalOpen, setActiveModalOpen] = useState(false);
  const [manageCards, setManageCards] = useState<OracleCard[]>([]);
  const [draftActive, setDraftActive] = useState<Record<string, boolean>>({});
  const [activeModalLoading, setActiveModalLoading] = useState(false);
  const [saveActiveLoading, setSaveActiveLoading] = useState(false);
  const initialActiveRef = useRef<Record<string, boolean>>({});

  const [deleteModal, setDeleteModal] = useState<{ show: boolean; id: string; name: string }>({
    show: false,
    id: '',
    name: '',
  });

  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    loadCards();
  }, [page, search]);

  const loadCards = async () => {
    setLoading(true);
    try {
      const res = await oracleCardsService.getAll({
        page,
        limit,
        search: search || undefined,
      });
      setCards(res.items || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load cards');
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const openActiveModal = async () => {
    setActiveModalOpen(true);
    setActiveModalLoading(true);
    try {
      const res = await oracleCardsService.getAll({ page: 1, limit: 500 });
      const list = res.items || [];
      setManageCards(list);
      const map: Record<string, boolean> = {};
      list.forEach((c: OracleCard) => {
        map[c._id] = c.isActive !== false;
      });
      initialActiveRef.current = { ...map };
      setDraftActive(map);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load cards');
      setManageCards([]);
      setDraftActive({});
    } finally {
      setActiveModalLoading(false);
    }
  };

  const toggleDraftActive = (cardId: string) => {
    setDraftActive((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const handleSaveActive = async () => {
    const tasks: Promise<unknown>[] = [];
    for (const c of manageCards) {
      const id = c._id;
      const next = !!draftActive[id];
      const prev = !!initialActiveRef.current[id];
      if (next !== prev) {
        tasks.push(oracleCardsService.setIsActive(id, next));
      }
    }

    if (tasks.length === 0) {
      toast.info('No changes to save');
      setActiveModalOpen(false);
      return;
    }

    setSaveActiveLoading(true);
    try {
      await Promise.all(tasks);
      toast.success('Active status saved');
      setActiveModalOpen(false);
      loadCards();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaveActiveLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await oracleCardsService.delete(deleteModal.id);
      toast.success('Card deleted');
      loadCards();
      setDeleteModal({ show: false, id: '', name: '' });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete card');
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb pageTitle="Oracle Cards" title="Oracle Cards" />
        <Row>
          <Col xs={12}>
            <MainImagePanel />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                  <h5 className="card-title mb-0">
                    <i className="ri-vip-crown-line me-2"></i>
                    Oracle Cards
                    {!loading && (
                      <Badge color="primary" pill className="ms-2 fs-6 fw-normal align-middle">
                        {total}
                      </Badge>
                    )}
                  </h5>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button color="primary" onClick={() => navigate('/dashboard/oracle-cards/add')}>
                      <i className="ri-add-line me-1"></i>
                      Add Card
                    </Button>
                    <Button color="secondary" outline onClick={openActiveModal}>
                      <i className="ri-toggle-line me-1"></i>
                      Active / inactive
                    </Button>
                  </div>
                </div>
                <p className="text-muted small mb-3">
                  Turn cards on or off for the app deck. Only active cards are returned to users; there is no
                  date-based schedule.
                </p>
                <div className="mb-3">
                  <Input
                    type="search"
                    placeholder="Search cards..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="form-control"
                    style={{ maxWidth: 280 }}
                  />
                </div>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">Loading cards...</p>
                  </div>
                ) : (
                  <>
                    <Table className="table table-hover table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 60 }}>Front</th>
                          <th style={{ width: 60 }}>Back</th>
                          <th>Title</th>
                          <th style={{ width: 120 }}>Status</th>
                          <th className="text-end" style={{ width: 140 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cards.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-4">
                              No cards yet. Add a card to get started.
                            </td>
                          </tr>
                        ) : (
                          cards.map((card) => (
                            <tr key={card._id}>
                              <td>
                                {card.frontImage || card.image ? (
                                  <img
                                    src={(card.frontImage || card.image)!}
                                    alt={`${card.title} front`}
                                    role="button"
                                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }}
                                    onClick={() => setLightbox({ src: (card.frontImage || card.image)!, alt: `${card.title} front` })}
                                  />
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {card.backImage ? (
                                  <img
                                    src={card.backImage}
                                    alt={`${card.title} back`}
                                    role="button"
                                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }}
                                    onClick={() => setLightbox({ src: card.backImage!, alt: `${card.title} back` })}
                                  />
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                <span className="fw-medium">{card.title}</span>
                                {card.shortDesc && (
                                  <p className="mb-0 text-muted small text-truncate" style={{ maxWidth: 200 }}>
                                    {card.shortDesc}
                                  </p>
                                )}
                              </td>
                              <td>
                                {card.isActive !== false ? (
                                  <Badge color="success" pill className="px-2">
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge color="secondary" pill className="px-2">
                                    Inactive
                                  </Badge>
                                )}
                              </td>
                              <td className="text-end">
                                <Button
                                  color="light"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => navigate(`/dashboard/oracle-cards/edit/${card._id}`)}
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() =>
                                    setDeleteModal({
                                      show: true,
                                      id: card._id,
                                      name: card.title,
                                    })
                                  }
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                    {totalPages > 1 && (
                      <div className="pagination-bar">
                        <div className="text-muted small mb-0">
                          Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                        </div>
                        <ul className="pagination pagination-rounded mb-0 ms-auto">
                          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                            <span
                              role="button"
                              className="page-link"
                              onClick={() => page > 1 && setPage((p) => p - 1)}
                              tabIndex={page <= 1 ? -1 : 0}
                            >
                              Previous
                            </span>
                          </li>
                          <li className="page-item active">
                            <span className="page-link">{page}</span>
                          </li>
                          <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                            <span
                              role="button"
                              className="page-link"
                              onClick={() => page < totalPages && setPage((p) => p + 1)}
                              tabIndex={page >= totalPages ? -1 : 0}
                            >
                              Next
                            </span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={activeModalOpen} toggle={() => setActiveModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setActiveModalOpen(false)}>
          Which cards are active in the app?
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Use the switch for each card, then click <strong>Save</strong>. Inactive cards stay in the admin list
            but are hidden from users.
          </p>
          {activeModalLoading ? (
            <div className="py-4 text-center">
              <Spinner color="primary" />
              <p className="mt-2 text-muted mb-0">Loading cards...</p>
            </div>
          ) : manageCards.length === 0 ? (
            <p className="text-muted mb-0">No cards yet. Add cards first.</p>
          ) : (
            <div className="border rounded p-3" style={{ maxHeight: 360, overflowY: 'auto' }}>
              {manageCards.map((card) => {
                const thumb = cardThumb(card);
                const on = !!draftActive[card._id];
                return (
                  <div
                    key={card._id}
                    className="d-flex align-items-center gap-3 py-2 border-bottom"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleDraftActive(card._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleDraftActive(card._id);
                      }
                    }}
                  >
                    {thumb && (
                      <img
                        src={thumb}
                        alt=""
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                      />
                    )}
                    <span className="flex-grow-1">{card.title}</span>
                    <div
                      className="d-flex align-items-center gap-2 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className={`small ${on ? 'text-success' : 'text-muted'}`}>
                        {on ? 'Active' : 'Inactive'}
                      </span>
                      <div className="form-check form-switch mb-0">
                        <Input
                          type="switch"
                          role="switch"
                          checked={on}
                          onChange={() => toggleDraftActive(card._id)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setActiveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSaveActive}
              disabled={saveActiveLoading || activeModalLoading || manageCards.length === 0}
            >
              {saveActiveLoading ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <DeleteModal
        show={deleteModal.show}
        recordName={deleteModal.name}
        onCloseClick={() => setDeleteModal({ show: false, id: '', name: '' })}
        onDeleteClick={confirmDelete}
      />

      <Modal
        isOpen={!!lightbox}
        toggle={() => setLightbox(null)}
        centered
        size="lg"
        contentClassName="bg-transparent border-0 shadow-none"
      >
        <div
          style={{ position: 'relative', textAlign: 'center', cursor: 'pointer' }}
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute',
              top: -16,
              right: -16,
              zIndex: 10,
              background: 'rgba(0,0,0,0.6)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              color: '#fff',
              fontSize: 20,
              lineHeight: '36px',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
          {lightbox && (
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: 8,
                display: 'block',
                margin: '0 auto',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default OracleCardsPage;
